import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSessionContext } from '@/providers/SessionProvider';
import { SessionLoadingState, SessionErrorState } from '@/components/SessionLoadingState';
import { MessageList } from '@/components/MessageList';
import { ChatInput } from '@/components/ChatInput';
import { ConnectionStatus } from '@/components/ConnectionStatus';
import { WelcomeStateLoader } from '@/components/WelcomeStateLoader';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, X, MessageSquare } from 'lucide-react';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { useTouchGestures } from '@/hooks/useTouchGestures';
import { useAppStore } from '@/stores/appStore';
import { useAgentConfig } from '@/hooks/useAgentConfig';
import { cn } from '@/lib/utils';

import { useStreaming } from '@/hooks/useStreaming';
import { eventsToMessages, createUserMessage, generateMessageId } from '@/lib/messageUtils';
import type { Message } from '@/types';

export function ChatPage() {
    // ALL HOOKS MUST BE CALLED FIRST - before any conditional returns
    const { sessionId } = useParams();
    const {
        currentSession,
        isLoading,
        error,
        createNewSession,
        createSessionWithMessage,
        isInWelcomeState,
        sessionCreationError,
        retrySessionCreation,
        clearError
    } = useSessionContext();
    const { ui, toggleSidebar } = useAppStore();
    const { isMobile, isSmallMobile } = useBreakpoint();
    const { config: agentConfig } = useAgentConfig(); // Add agent configuration
    const [messages, setMessages] = useState<Message[]>([]);
    const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
    const [streamingMessage, setStreamingMessage] = useState<{
        content: string;
        isComplete: boolean;
        timestamp: Date;
    } | null>(null);
    const [pendingMessage, setPendingMessage] = useState<string>('');
    const [isCreatingSession, setIsCreatingSession] = useState(false);

    const streaming = useStreaming({
        onMessage: (content, isComplete) => {
            // Hide loading indicator when we start receiving content
            setIsWaitingForResponse(false);
            setStreamingMessage({
                content,
                isComplete,
                timestamp: new Date(),
            });
        },
        onComplete: (events) => {
            // Convert response events to messages and add to UI
            const responseMessages = eventsToMessages(events);
            setMessages(prev => [...prev, ...responseMessages]);
            setStreamingMessage(null);
            setIsWaitingForResponse(false);
        },
        onError: (error) => {
            console.error('Streaming error:', error);
            setStreamingMessage(null);
            setIsWaitingForResponse(false);
        },
    });

    // Touch gestures for mobile navigation
    const touchRef = useTouchGestures({
        onSwipeRight: () => {
            if (isMobile && !ui.sidebarOpen) {
                toggleSidebar();
            }
        },
        threshold: 100,
    });

    // Convert session events to messages when session loads
    useEffect(() => {
        if (currentSession?.events) {
            const sessionMessages = eventsToMessages(currentSession.events);
            setMessages(sessionMessages);
        } else {
            setMessages([]);
        }
    }, [currentSession]);

    // NOW we can do conditional returns after all hooks are called

    // Show loading state while session is being loaded or created
    if (isLoading) {
        return <SessionLoadingState message={sessionId ? 'Loading session...' : 'Creating new session...'} />;
    }

    // Show error state if session failed to load
    if (error) {
        return (
            <SessionErrorState
                error={error}
                onRetry={() => sessionId ? window.location.reload() : createNewSession()}
            />
        );
    }

    const handleWelcomeMessage = async (messageText: string) => {
        if (!messageText.trim() || isLoading || isCreatingSession) return;

        try {
            // Store the message in case we need to retry
            setPendingMessage(messageText);
            setIsCreatingSession(true);

            // Create session with the first message
            await createSessionWithMessage(messageText);

            // Clear pending message on success
            setPendingMessage('');

            // Navigation to the new session is handled by createSessionWithMessage
        } catch (error) {
            console.error('Failed to create session with message:', error);
            // Error handling is managed by SessionProvider
            // Keep the message in pendingMessage for retry
        } finally {
            setIsCreatingSession(false);
        }
    };

    const handleRetrySessionCreation = async () => {
        if (!pendingMessage.trim() || isCreatingSession) return;

        try {
            setIsCreatingSession(true);
            await retrySessionCreation(pendingMessage);
            setPendingMessage('');
        } catch (error) {
            console.error('Retry failed:', error);
            // Error is handled by SessionProvider
        } finally {
            setIsCreatingSession(false);
        }
    };

    const handleClearSessionError = () => {
        clearError();
        setPendingMessage('');
    };

    // Show welcome state if in welcome state (no session but authenticated)
    if (isInWelcomeState) {
        return (
            <div className={cn(
                "flex h-full flex-col bg-gradient-to-br from-background via-background to-muted/20",
                isMobile && "mobile-chat-layout"
            )}>
                {/* Session creation error alert */}
                {sessionCreationError && (
                    <div className="border-b flex-shrink-0 bg-background/95 backdrop-blur-sm">
                        <div className="max-w-4xl mx-auto p-2 sm:p-4">
                            <Alert variant="destructive" className="border-destructive/50 bg-destructive/5">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="flex items-center justify-between">
                                    <span className={`${isSmallMobile ? 'text-xs' : 'text-sm'}`}>
                                        Failed to start conversation: {sessionCreationError}
                                    </span>
                                    <div className="flex gap-2 ml-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleRetrySessionCreation}
                                            disabled={isLoading || !pendingMessage.trim()}
                                            className="h-6 text-xs border-destructive/30 hover:bg-destructive/10"
                                        >
                                            Retry
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleClearSessionError}
                                            className="h-6 w-6 p-0 flex-shrink-0 hover:bg-destructive/10"
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </AlertDescription>
                            </Alert>
                        </div>
                    </div>
                )}

                {/* Welcome message area with enhanced visual design */}
                <div className="flex-1 flex items-center justify-center relative overflow-hidden">
                    {/* Background decoration */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary rounded-full blur-3xl"></div>
                        <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-primary rounded-full blur-2xl"></div>
                    </div>

                    <div className={cn(
                        "text-center mx-auto relative z-10",
                        isMobile ? "max-w-sm px-4" : "max-w-md px-6"
                    )}>
                        {/* Welcome header with visual emphasis */}
                        <div className="mb-6">
                            <div className={cn(
                                "inline-flex items-center justify-center rounded-full bg-primary/10 mb-4",
                                isMobile ? "w-16 h-16" : "w-20 h-20"
                            )}>
                                <MessageSquare className={cn(
                                    "text-primary",
                                    isMobile ? "h-8 w-8" : "h-10 w-10"
                                )} />
                            </div>
                            <h2 className={cn(
                                "font-bold text-foreground mb-3",
                                isMobile ? "text-xl" : "text-2xl"
                            )}>
                                Welcome to Wrnt AI Agent
                            </h2>
                            <p className={cn(
                                "text-muted-foreground leading-relaxed",
                                isMobile ? "text-sm" : "text-base"
                            )}>
                                Your intelligent assistant for analyzing warrant documents and legal proceedings.
                            </p>
                        </div>

                        {/* Capabilities showcase */}
                        <div className={cn(
                            "text-left mb-6 p-4 rounded-lg bg-card/50 border border-border/50",
                            isMobile ? "space-y-3" : "space-y-4"
                        )}>
                            <h4 className={cn(
                                "font-semibold text-foreground flex items-center gap-2",
                                isMobile ? "text-sm" : "text-base"
                            )}>
                                <div className="w-2 h-2 bg-primary rounded-full"></div>
                                What I can help you with:
                            </h4>
                            <ul className={cn(
                                "text-muted-foreground space-y-2",
                                isMobile ? "text-xs" : "text-sm"
                            )}>
                                <li className="flex items-start gap-2">
                                    <div className="w-1 h-1 bg-muted-foreground rounded-full mt-2 flex-shrink-0"></div>
                                    <span>Analyze warrant terms and conditions</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1 h-1 bg-muted-foreground rounded-full mt-2 flex-shrink-0"></div>
                                    <span>Summarize complex legal documents</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1 h-1 bg-muted-foreground rounded-full mt-2 flex-shrink-0"></div>
                                    <span>Verify compliance requirements</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1 h-1 bg-muted-foreground rounded-full mt-2 flex-shrink-0"></div>
                                    <span>Compare multiple warrant documents</span>
                                </li>
                            </ul>
                        </div>

                        {/* Call to action */}
                        <div className={cn(
                            "p-3 rounded-lg bg-primary/5 border border-primary/20",
                            isMobile ? "text-xs" : "text-sm"
                        )}>
                            <p className="text-muted-foreground">
                                <span className="font-medium text-foreground">Ready to start?</span>
                                <br />
                                Type your question below to begin a new conversation.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Enhanced chat input for welcome state */}
                <div className="flex-shrink-0">
                    <ChatInput
                        onSendMessage={handleWelcomeMessage}
                        onCreateSessionWithMessage={handleWelcomeMessage}
                        sessionState="none"
                        disabled={isLoading || isCreatingSession}
                        isWaitingForResponse={isCreatingSession}
                        placeholder={isMobile
                            ? "Ask about your warrant documents..."
                            : "Type your first message to start a new conversation..."
                        }
                        error={sessionCreationError}
                        onRetry={handleRetrySessionCreation}
                        initialValue={pendingMessage}
                    />
                </div>

                {/* Session creation loading overlay */}
                {isCreatingSession && (
                    <WelcomeStateLoader message="Starting your conversation..." />
                )}
            </div>
        );
    }

    const handleSendMessage = async (messageText: string) => {
        if (!sessionId || !messageText.trim() || streaming.isStreaming || isWaitingForResponse) return;

        // Add user message to UI immediately
        const userMessage: Message = {
            id: generateMessageId(),
            content: messageText,
            role: 'user',
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);

        // Show loading indicator
        setIsWaitingForResponse(true);

        // Start streaming the response with agent configuration
        const content = createUserMessage(messageText);
        await streaming.streamMessage(sessionId, content, agentConfig);
    };

    const handleCopy = (content: string) => {
        navigator.clipboard.writeText(content);
        // TODO: Show success toast
    };

    // Main chat interface
    return (
        <div ref={touchRef} className={cn(
            "flex h-full flex-col min-h-0",
            isMobile && "mobile-chat-layout"
        )}>
            {/* Session info header */}
            {currentSession && (
                <div className="border-b p-2 sm:p-4 bg-muted/30 flex-shrink-0">
                    <div className="max-w-4xl mx-auto flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                            <h3 className={`font-medium text-muted-foreground ${isSmallMobile ? 'text-xs' : 'text-sm'}`}>
                                Session: {currentSession.id.slice(0, isSmallMobile ? 6 : 8)}
                            </h3>
                            {!isSmallMobile && (
                                <p className="text-xs text-muted-foreground">
                                    {currentSession.events?.length || 0} events •
                                    Updated {currentSession.lastUpdateTime ? new Date(currentSession.lastUpdateTime).toLocaleString() : 'Unknown'}
                                </p>
                            )}
                        </div>
                        <ConnectionStatus />
                    </div>
                </div>
            )}

            {/* Streaming error alert */}
            {streaming.error && (
                <div className="border-b flex-shrink-0">
                    <div className="max-w-4xl mx-auto p-2 sm:p-4">
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="flex items-center justify-between">
                                <span className={`${isSmallMobile ? 'text-xs' : 'text-sm'}`}>
                                    {streaming.error}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={streaming.resetError}
                                    className="h-6 w-6 p-0 flex-shrink-0"
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            </AlertDescription>
                        </Alert>
                    </div>
                </div>
            )}

            {/* Chat messages - scrollable area */}
            <div className={cn(
                "flex-1 overflow-hidden",
                isMobile && "mobile-messages-container"
            )}>
                <MessageList
                    messages={messages}
                    isLoading={false}
                    isTyping={isWaitingForResponse}
                    streamingMessage={streamingMessage}
                    onCopy={handleCopy}
                />
            </div>

            {/* Chat input */}
            <div className="flex-shrink-0">
                <ChatInput
                    onSendMessage={handleSendMessage}
                    onCancelStream={streaming.cancelStream}
                    sessionState="active"
                    disabled={streaming.isStreaming || isWaitingForResponse}
                    isStreaming={streaming.isStreaming}
                    isWaitingForResponse={isWaitingForResponse}
                    placeholder="Ask a question about your warrant documents..."
                />
            </div>
        </div>
    );
}